import {getSafeTranslation} from 'helpers/localization.helper.ts';
import {
	Modifier,
	ModifierGroup,
	ModifierGroupSelections,
	MenuEntryModifierSelections,
	ModifierSelection, ModifierGroupId
} from './modifier.helper.types';
import {Translations} from './localization.helper.types';

export const getModifierValidationErrors = (selectedModifiers: MenuEntryModifierSelections, modifierGroups: Array<ModifierGroup>, translations: Translations): string => {
	return Object.keys(selectedModifiers)
		.map(modifierGroupId => {
			const modifierGroup = modifierGroups.find(
				modifierGroup => modifierGroup.modifierGroupId.toString() === modifierGroupId.toString()
			);

			if (selectedModifiers[modifierGroupId].length < modifierGroup.min) {
				return getSafeTranslation('ModifierValidationErrorMessagePopup.description', translations)
					.replace('{modifierName}', modifierGroup.name)
					.replace('{minQuantity}', modifierGroup.min?.toString());
			} else {
				return null;
			}
		})
		.filter(message => !!message)
		.join('{n}');
};

export const getSelectedModifiersFromGroup = (modifierGroup: ModifierGroup, modifierGroupSelections: ModifierGroupSelections = []): Array<Modifier> => {
	const selectedModifierIds = modifierGroupSelections.map(modifierSelection => modifierSelection.modifierId);
	return modifierGroup.modifiers.filter(modifier => selectedModifierIds.includes(modifier.modifierId));
};

export const getDefaultModifierSelections = (modifierGroups: Array<ModifierGroup> = []): MenuEntryModifierSelections => {
	const result = {};

	modifierGroups.forEach(modifierGroup => {
		result[modifierGroup.modifierGroupId] = modifierGroup.modifiers
			.filter(modifier => modifier.defaultQuantity > 0)
			.map(modifier => ({
				modifierId: modifier.modifierId,
				quantity: modifier.defaultQuantity
			}));
	});

	return result;
};

export const getModifierSelectionsFilteredByIds = (
	modifierSelections: MenuEntryModifierSelections,
	modifierGroupIds: Array<ModifierGroupId> = []
): MenuEntryModifierSelections => {

	return modifierGroupIds
		.reduce((accum, modifierGroupId) => {
			return {
				...accum,
				[modifierGroupId]: modifierSelections[modifierGroupId]
			};
		}, {});
};

export function getModifierSelectionPrice(modifier: Modifier, modifierSelection?: ModifierSelection) {
	const quantityWithPrice = modifier.freeQuantity && modifierSelection?.quantity
		? Math.max(0, modifierSelection.quantity - modifier.freeQuantity)
		: modifierSelection?.quantity;

	return modifier.price * (quantityWithPrice || 0);
}

function getModifierGroupPrice(modifierGroup: ModifierGroup, modifierGroupSelections: ModifierGroupSelections = []): number {
	if (modifierGroup.allowToChangeQuantity) {
		return modifierGroup.modifiers.reduce((accum, modifier) => {
			const modifierSelection = modifierGroupSelections.find(modifierSelection => modifierSelection.modifierId === modifier.modifierId);
			return accum + getModifierSelectionPrice(modifier, modifierSelection);
		}, 0);
	}

	const selectedModifierIds = modifierGroupSelections.map(modifierSelection => modifierSelection.modifierId);

	return modifierGroup.modifiers.reduce((accum, modifier) => {
		return selectedModifierIds.includes(modifier.modifierId) ? accum + modifier.price : accum;
	}, 0);
}

export const getModifiersPrice = (modifierGroups: Array<ModifierGroup> = [], modifierSelections: MenuEntryModifierSelections): number => {
	return modifierGroups.reduce((accum, modifierGroup) => {
		return accum + getModifierGroupPrice(modifierGroup, modifierSelections[modifierGroup.modifierGroupId]);
	}, 0);
};

export const getModifierComputedName = (modifier: Modifier): string => {
	return `${modifier.name} (+${modifier.dPrice})`;
};
